import React, { useState } from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { isMobile } from "../../utils/appUtils";
import { useLanguage } from '../../context/LanguageContext';
import { booksTemplateTranslations, getBooksTranslations, BooksTranslations } from '../../translations/booksTemplate';
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";

interface BookLanguage {
  label: string;
  value: string;
  pdfPath: string;
}

interface BookData {
  id: string;
  title: string;
  languages: BookLanguage[];
}

const booksData: BookData[] = [
  {
    id: 'philosophy',
    title: 'Philosophy of Time',
    languages: [
      { label: 'English', value: 'en', pdfPath: '/books-pdf/book_Niveau_eleve_Philosophie_der_Zeit_englisch.pdf' },
      { label: 'French', value: 'fr', pdfPath: '/books-pdf/book_Niveau_eleve_Philosophie_der_Zeit_franzoesisch.pdf' },
      { label: 'German', value: 'de', pdfPath: '/books-pdf/book_Niveau_eleve_Philosophie_der_Zeit_deutsch.pdf' },
      { label: 'Chinese', value: 'cn', pdfPath: '/books-pdf/book_Niveau_eleve_Philosophie_der_Zeit_chinesisch.pdf' },
    ],
  },
  {
    id: 'timeIsMine',
    title: 'Time is Mine',
    languages: [
      { label: 'English', value: 'en', pdfPath: '/books-pdf/book_Niveau_eleve_Time_is_mine_englisch.pdf' },
      { label: 'German', value: 'de', pdfPath: '/books-pdf/book_Niveau élevé_Time is mine_DE.pdf' },
    ],
  },
  {
    id: 'tourmaline',
    title: 'The Secret of Tourmaline',
    languages: [
      { label: 'English', value: 'en', pdfPath: '/books-pdf/book_Turmalin_E_noch nicht korrigiert.pdf' },
      { label: 'German', value: 'de', pdfPath: '/books-pdf/book_Turmalin_DE.pdf' },
      { label: 'French', value: 'fr', pdfPath: '/books-pdf/book_Turmalin_F_noch nicht korrigiert.pdf' },
      { label: 'Chinese', value: 'cn', pdfPath: '/books-pdf/book_Turmalin_CHIN.pdf' },
    ],
  },
  {
    id: 'mysticalGem',
    title: 'The Mystical Gem',
    languages: [
      { label: 'English', value: 'en', pdfPath: '/books-pdf/book_Diamantschleiferei_Michael_Bonke_diamond_the_mystical_gem_english.pdf' },
      { label: 'German', value: 'de', pdfPath: '/books-pdf/book_Diamantschleiferei_Michael_Bonke_mystik_des_diamanten_deutsch.pdf' },
    ],
  },
];

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
  languages: BookLanguage[];
  onSelect: (pdfPath: string) => void;
  bookTitle: string;
  selectLanguageText: string;
}

const LanguageModal: React.FC<LanguageModalProps> = ({ isOpen, onClose, languages, onSelect, bookTitle, selectLanguageText }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 relative" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h3 className="text-xl font-semibold mb-4 pr-8">{bookTitle}</h3>
        <p className="text-gray-600 mb-4">{selectLanguageText}</p>
        <div className="space-y-2">
          {languages.map((lang) => (
            <button
              key={lang.value}
              onClick={() => onSelect(lang.pdfPath)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded transition-colors"
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Books: React.FC = () => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    bookId: string | null;
  }>({
    isOpen: false,
    bookId: null,
  });

  const { language } = useLanguage();
  const { isEditMode } = useEditMode();
  const { content, loading } = usePageContent<BooksTranslations>('books');

  // Use translations as fallback content
  const t = content || getBooksTranslations(language);

  // Show loading state if needed
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  const handleDownload = (bookId: string) => {
    const book = booksData.find(b => b.id === bookId);
    if (!book) return;
    setModalState({ isOpen: true, bookId });
  };

  const handleLanguageSelect = (pdfPath: string) => {
    window.open(pdfPath, '_blank');
    setModalState({ isOpen: false, bookId: null });
  };

  const currentBook = modalState.bookId ? booksData.find(b => b.id === modalState.bookId) : null;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <LanguageModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, bookId: null })}
        languages={currentBook?.languages || []}
        onSelect={handleLanguageSelect}
        bookTitle={currentBook?.title || ''}
        selectLanguageText={t.selectLanguage}
      />

      <BackgroundCover
        backgroundHeight={isMobile() ? 400 : undefined}
        imagePosition="center"
        backgroundImage="/images/books_background.jpg"
        isWatchPage={true}
      />

      <div className="block md:hidden px-4 pt-8">
        <Title
          firstLine={isEditMode ? (
            <EditableText
              contentKey="books.pageTitle.firstLine"
              defaultValue={t.pageTitle.firstLine}
            />
          ) : t.pageTitle.firstLine}
          secondLine={isEditMode ? (
            <EditableText
              contentKey="books.pageTitle.secondLine"
              defaultValue={t.pageTitle.secondLine}
            />
          ) : t.pageTitle.secondLine}
          className="mb-8"
          animate={true}
        />
      </div>

      <div className="w-full px-4 md:px-0 py-8 md:py-16">
        <div className="flex flex-col items-center">
          <div className="hidden md:block">
            <Title
              firstLine={isEditMode ? (
                <EditableText
                  contentKey="books.pageTitle.firstLine"
                  defaultValue={t.pageTitle.firstLine}
                />
              ) : t.pageTitle.firstLine}
              secondLine={isEditMode ? (
                <EditableText
                  contentKey="books.pageTitle.secondLine"
                  defaultValue={t.pageTitle.secondLine}
                />
              ) : t.pageTitle.secondLine}
              className="mb-16"
              animate={true}
            />
          </div>
          <div className="px-4 md:px-24 text-sm text-gray-600 leading-relaxed max-w-[2000px] mx-auto">
            {isEditMode ? (
              <EditableText
                contentKey="books.mainDescription"
                defaultValue={t.mainDescription}
                multiline={true}
              />
            ) : t.mainDescription}
          </div>
        </div>
      </div>

      <div className="relative w-full">
        <div className="w-full bg-white py-8 md:py-12">
          <div className="max-w-[2000px] mx-auto px-4 md:px-24">
            <TitleWithDescription
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="books.discoverTitle.firstLine"
                    defaultValue={t.discoverTitle.firstLine}
                  />
                ) : t.discoverTitle.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="books.discoverTitle.secondLine"
                    defaultValue={t.discoverTitle.secondLine}
                  />
                ) : t.discoverTitle.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="books.discoverDescription"
                  defaultValue={t.discoverDescription}
                  multiline={true}
                />
              ) : t.discoverDescription}
              layout="row"
              className="max-w-7xl mx-auto"
            />
          </div>

          <div className="max-w-[2000px] mx-auto px-4 md:px-12 space-y-8 md:space-y-12">
            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="books.philosophyTitle.firstLine"
                      defaultValue={t.philosophyTitle.firstLine}
                    />
                  ) : t.philosophyTitle.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="books.philosophyTitle.secondLine"
                      defaultValue={t.philosophyTitle.secondLine}
                    />
                  ) : t.philosophyTitle.secondLine,
                }}
                description={isEditMode ? (
                  <EditableText
                    contentKey="books.philosophyDescription"
                    defaultValue={t.philosophyDescription}
                    multiline={true}
                  />
                ) : t.philosophyDescription}
                media={{
                  type: 'image',
                  src: "/images/books_sub_philosophy_of_time_book.jpg",
                  alt: "Philosophy of Time Book"
                }}
                mediaPosition="right"
                extraContent={
                  <div className="flex gap-4 mt-6 md:mt-10">
                    <button
                      onClick={() => handleDownload('philosophy')}
                      className="px-4 md:px-6 py-2 mb-8 -mt-8 md:mb-0 bg-black text-white hover:bg-gray-800 transition-colors text-sm md:text-sm"
                    >
                      {isEditMode ? (
                        <EditableText
                          contentKey="books.readBook"
                          defaultValue={t.readBook}
                        />
                      ) : t.readBook}
                    </button>
                  </div>
                }
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="books.timeIsMineTitle.firstLine"
                      defaultValue={t.timeIsMineTitle.firstLine}
                    />
                  ) : t.timeIsMineTitle.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="books.timeIsMineTitle.secondLine"
                      defaultValue={t.timeIsMineTitle.secondLine}
                    />
                  ) : t.timeIsMineTitle.secondLine,
                }}
                description={isEditMode ? (
                  <EditableText
                    contentKey="books.timeIsMineDescription"
                    defaultValue={t.timeIsMineDescription}
                    multiline={true}
                  />
                ) : t.timeIsMineDescription}
                media={{
                  type: 'image',
                  src: "/images/books_sub_time_is_mine_book.jpg",
                  alt: "Time is Mine Book"
                }}
                mediaPosition="left"
                extraContent={
                  <div className="flex gap-4 mt-6 md:mt-10">
                    <button
                      onClick={() => handleDownload('timeIsMine')}
                      className="px-4 md:px-6 py-2 mb-8 -mt-8 md:mb-0 bg-black text-white hover:bg-gray-800 transition-colors text-sm md:text-sm"
                    >
                      {isEditMode ? (
                        <EditableText
                          contentKey="books.readBook"
                          defaultValue={t.readBook}
                        />
                      ) : t.readBook}
                    </button>
                  </div>
                }
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="books.tourmalineTitle.firstLine"
                      defaultValue={t.tourmalineTitle.firstLine}
                    />
                  ) : t.tourmalineTitle.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="books.tourmalineTitle.secondLine"
                      defaultValue={t.tourmalineTitle.secondLine}
                    />
                  ) : t.tourmalineTitle.secondLine,
                }}
                description={isEditMode ? (
                  <EditableText
                    contentKey="books.tourmalineDescription"
                    defaultValue={t.tourmalineDescription}
                    multiline={true}
                  />
                ) : t.tourmalineDescription}
                media={{
                  type: 'image',
                  src: "/images/books_sub_tourmaline_book.jpg",
                  alt: "The Secret of Tourmaline Book"
                }}
                mediaPosition="right"
                extraContent={
                  <div className="flex gap-4 mt-6 md:mt-10">
                    <button
                      onClick={() => handleDownload('tourmaline')}
                      className="px-4 md:px-6 py-2 mb-8 -mt-8 md:mb-0 bg-black text-white hover:bg-gray-800 transition-colors text-sm md:text-sm"
                    >
                      {isEditMode ? (
                        <EditableText
                          contentKey="books.readBook"
                          defaultValue={t.readBook}
                        />
                      ) : t.readBook}
                    </button>
                  </div>
                }
                showLine={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Books;
